"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Play,
  Volume2,
  VolumeX,
  Maximize2,
  X,
  Pause,
} from "lucide-react";
import { Blurhash } from "react-blurhash";

const DEFAULT_BLURHASH = "UGF=Hx~VD$NG0Kf4-;x]yY%MD%V@VDM{=|s:";

type Props = {
  src: string;
  thumbnailUrl?: string;
  blurhash?: string;
  width?: string;
  height?: string;
  className?: string;
  flag?: React.ReactNode;
  language?: string;
  title?: string;
  playing?: boolean;
  onPlay?: () => void;
};

const Mp4AdvancedPlayer: React.FC<Props> = ({
  src,
  thumbnailUrl,
  blurhash = DEFAULT_BLURHASH,
  className = "",
  language,
  flag,
  title,
  playing = false,
  onPlay,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();

  // Main video states
  const [hasEnded, setHasEnded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [isVideoInitialized, setIsVideoInitialized] = useState(false);

  // Fullscreen states
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenMuted, setFullscreenMuted] = useState(true);
  const [fullscreenPlaying, setFullscreenPlaying] = useState(false);
  const [fullscreenEnded, setFullscreenEnded] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);

  // Initialize video
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src || isVideoInitialized) return;

    video.preload = "none";
    video.src = src;
    video.muted = muted;
    setIsVideoInitialized(true);
  }, [src, muted, isVideoInitialized]);

  // Load MP4 source for fullscreen video
  useEffect(() => {
    if (!isFullscreen) return;

    const video = fullscreenVideoRef.current;
    if (!video || !src) return;

    video.src = src;
    video.load();
  }, [isFullscreen, src]);

  // Control video play/pause based on playing prop
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoInitialized || isFullscreen) return;

    if (playing) {
      video.preload = "auto";
      video.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    } else if (video.readyState > 0) {
      video.pause();
    }
  }, [playing, isVideoInitialized, isFullscreen]);

  // Main video play/pause click handler
  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      
      const video = videoRef.current;
      if (!video) return;

      if (hasEnded) {
        video.currentTime = 0;
        setHasEnded(false);
      }

      // Always call onPlay - parent will handle toggle logic
      onPlay?.();
    },
    [hasEnded, onPlay]
  );

  // Fullscreen play/pause
  const toggleFullscreenPlayPause = useCallback(() => {
    const video = fullscreenVideoRef.current;
    if (!video) return;

    if (fullscreenEnded) {
      video.currentTime = 0;
      setFullscreenEnded(false);
      video.play();
      setFullscreenPlaying(true);
    } else if (fullscreenPlaying) {
      video.pause();
      setFullscreenPlaying(false);
    } else {
      video.play();
      setFullscreenPlaying(true);
    }
  }, [fullscreenPlaying, fullscreenEnded]);

  // Pause video when out of viewport
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && playing) {
          video.pause();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [playing]);

  // Mute toggles
  const handleMuteToggle = () => setMuted((prev) => !prev);
  const handleFullscreenMuteToggle = () => setFullscreenMuted((prev) => !prev);

  // Open fullscreen
  const openFullscreen = useCallback(() => {
    const normalVideo = videoRef.current;
    if (!normalVideo) return;

    setIsFullscreen(true);
    setFullscreenMuted(muted);
    setFullscreenEnded(false);
    normalVideo.pause();
  }, [muted]);

  // Close fullscreen
  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    setFullscreenPlaying(false);
    setFullscreenEnded(false);

    const normalVideo = videoRef.current;
    const fsVideo = fullscreenVideoRef.current;

    if (normalVideo && fsVideo) {
      normalVideo.currentTime = fsVideo.currentTime;
      normalVideo.muted = fullscreenMuted;
      setMuted(fullscreenMuted);

      if (playing) {
        normalVideo.play().catch((e) => {
          console.warn("Error resuming main video after fullscreen:", e);
        });
      }
    }

    if (document.exitFullscreen) {
      document.exitFullscreen().catch(console.error);
    }
  }, [fullscreenMuted, playing]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFsChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        closeFullscreen();
      }
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFsChange);
  }, [isFullscreen, closeFullscreen]);

  // Enter fullscreen and sync video
  useEffect(() => {
    if (!isFullscreen || !fullscreenVideoRef.current || !videoRef.current)
      return;

    const fsVideo = fullscreenVideoRef.current;
    const normalVideo = videoRef.current;

    fsVideo.currentTime = normalVideo.currentTime;
    fsVideo.muted = muted;

    const parent = fsVideo.parentElement;
    if (parent && parent.requestFullscreen) {
      parent.requestFullscreen().catch((err) => {
        console.error("Fullscreen request failed:", err);
      });
    }

    fsVideo
      .play()
      .then(() => setFullscreenPlaying(true))
      .catch(console.error);
  }, [isFullscreen, muted]);

  // Sync muted state
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (fullscreenVideoRef.current)
      fullscreenVideoRef.current.muted = fullscreenMuted;
  }, [fullscreenMuted]);

  // Fullscreen controls visibility
  const handleFullscreenMouseMove = useCallback(() => {
    setShowFullscreenControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (fullscreenPlaying) {
        setShowFullscreenControls(false);
      }
    }, 3000);
  }, [fullscreenPlaying]);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Helper to determine what should be visible
  const shouldShowThumbnail = !playing || !isVideoReady;

  return (
    <>
      {/* Main Video Player */}
      <div
        className={`relative w-full h-full overflow-hidden cursor-pointer ${className}`}
        onClick={handlePlayClick}
      >
        {/* Language Badge */}
        {language && flag && (
          <div className="absolute top-2.5 right-2.5 z-[49]">
            <div className="flex gap-x-2 items-center px-2 py-1 bg-white border border-orange-600 rounded-full text-black font-medium text-xs">
              {flag}
              {language}
            </div>
          </div>
        )}

        {/* Placeholder Layer */}
        {shouldShowThumbnail && (
          <div className="absolute inset-0 z-10">
            {!thumbLoaded && (
              <Blurhash
                hash={blurhash}
                resolutionX={32}
                resolutionY={32}
                punch={1}
                width="100%"
                height="100%"
                style={{ position: "absolute", inset: 0, zIndex: 10 }}
              />
            )}

            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt="thumbnail"
                onLoad={() => setThumbLoaded(true)}
                className={`object-cover w-full h-full absolute transition-opacity duration-300 z-20 ${
                  thumbLoaded ? "opacity-100" : "opacity-0"
                }`}
              />
            )}

            {playing && !isVideoReady && (
              <div className="absolute inset-0 z-30 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        )}

        {/* Main Video Element */}
        <video
          ref={videoRef}
          muted={muted}
          playsInline
          preload="none"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            playing && isVideoReady ? "opacity-100 z-40" : "opacity-0 z-0"
          }`}
          onCanPlay={() => setIsVideoReady(true)}
          onLoadedMetadata={() => setIsVideoReady(true)}
          onWaiting={() => setBuffering(true)}
          onPlaying={() => {
            setBuffering(false);
            setHasStartedPlaying(true);
          }}
          onEnded={() => {
            setHasEnded(true);
            setHasStartedPlaying(false);
          }}
          onError={(e) => console.error("Video error:", e)}
        />

        {/* Buffering Spinner */}
        {buffering && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Center Play/Replay Button */}
        {shouldShowThumbnail && (
          <div className="absolute inset-0 flex items-center justify-center z-30">
            <button onClick={handlePlayClick}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="24"
                  cy="24"
                  r="24"
                  fill="white"
                  fillOpacity="0.8"
                />
                <path
                  d="M32 24L20 30.9282L20 17.0718L32 24Z"
                  fill="#333333"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Controls */}
        {playing && (
          <div className="absolute bottom-4 right-4 flex gap-3 z-40">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMuteToggle();
              }}
              className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
              aria-label="Mute toggle"
            >
              {muted ? (
                <VolumeX size={18} color="white" />
              ) : (
                <Volume2 size={18} color="white" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openFullscreen();
              }}
              className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
              aria-label="Fullscreen"
            >
              <Maximize2 size={18} color="white" />
            </button>
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen &&
        createPortal(
          <div
            className="fixed inset-0 bg-black z-[9999] flex items-center justify-center"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeFullscreen();
            }}
            onMouseMove={handleFullscreenMouseMove}
            style={{ touchAction: "none" }}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Fullscreen Video */}
              <video
                ref={fullscreenVideoRef}
                className="w-full h-full object-contain"
                playsInline
                muted={fullscreenMuted}
                controls={false}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreenPlayPause();
                }}
                onEnded={() => {
                  setFullscreenEnded(true);
                  setFullscreenPlaying(false);
                }}
                onWaiting={() => setBuffering(true)}
                onPlaying={() => {
                  setBuffering(false);
                  setFullscreenEnded(false);
                }}
                onError={(e) => console.error("Fullscreen video error:", e)}
              />

              {/* Center Play/Pause/Replay Button */}
              {(!fullscreenPlaying || fullscreenEnded) && (
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer z-40"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreenPlayPause();
                  }}
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="24"
                      cy="24"
                      r="24"
                      fill="white"
                      fillOpacity="0.8"
                    />
                    <path
                      d="M32 24L20 30.9282L20 17.0718L32 24Z"
                      fill="#333333"
                    />
                  </svg>
                </div>
              )}

              {/* Top Controls Bar */}
              <div
                className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-6 transition-opacity duration-300 z-50 ${
                  showFullscreenControls
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                }`}
              >
                <div className="flex items-center justify-between">
                  {/* Title/Language Badge */}
                  <div className="flex items-center space-x-3">
                    {language && flag && (
                      <div className="flex gap-x-2 items-center px-3 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium text-sm">
                        {flag}
                        {language}
                      </div>
                    )}
                    {title && !language && (
                      <div className="px-3 py-2 bg-white/10 backdrop-blur-md text-sm text-white font-medium rounded-full border border-white/20 whitespace-nowrap">
                        {title}
                      </div>
                    )}
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={closeFullscreen}
                    className="text-white hover:scale-110 transition-transform duration-200"
                    aria-label="Close fullscreen"
                  >
                    <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                      <X size={20} />
                    </div>
                  </button>
                </div>
              </div>

              {/* Bottom Controls */}
              <div
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 transition-opacity duration-300 z-50 ${
                  showFullscreenControls
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                }`}
              >
                <div className="flex items-center justify-center space-x-4">
                  {/* Volume Toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFullscreenMuteToggle();
                    }}
                    className="text-white hover:scale-110 transition-transform duration-200"
                    aria-label="Mute toggle"
                  >
                    <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                      {fullscreenMuted ? (
                        <VolumeX size={20} />
                      ) : (
                        <Volume2 size={20} />
                      )}
                    </div>
                  </button>

                  {/* Play/Pause Toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFullscreenPlayPause();
                    }}
                    className="text-white hover:scale-110 transition-transform duration-200"
                    aria-label="Play/Pause"
                  >
                    <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                      {fullscreenPlaying ? (
                        <Pause size={20} />
                      ) : (
                        <Play size={20} />
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Mp4AdvancedPlayer;